// dBase III (.dbf) byte layout — header: byte 0 version (must be 0x03),
// bytes 4-7 record count (int32 LE), bytes 8-9 header length (int16 LE),
// bytes 10-11 record length (int16 LE). Field descriptors: 32 bytes each
// from byte 32, terminated by 0x0D — name (bytes 0-10), type (byte 11),
// length (byte 16), decimal count (byte 17). Records start at the header's
// declared length: 1 deletion-flag byte + each field's fixed-width text.

interface DbfField {
  name: string;
  type: "C" | "N" | "F" | "L" | "D";
  length: number;
  decimalCount: number;
}

interface ParsedDbf {
  fields: DbfField[];
  /** Deleted rows already excluded — keeps 1:1 alignment with a companion .shp's own records. */
  rows: Record<string, string>[];
}

const FIELD_TYPES = new Set(["C", "N", "F", "L", "D"]);

function isDbfFieldType(type: string): type is DbfField["type"] {
  return FIELD_TYPES.has(type);
}

function readAsciiZ(view: DataView, offset: number, length: number): string {
  const bytes = new Uint8Array(view.buffer, view.byteOffset + offset, length);
  const nul = bytes.indexOf(0);
  const slice = nul === -1 ? bytes : bytes.subarray(0, nul);
  return new TextDecoder("ascii").decode(slice);
}

export function parseDbf(buffer: ArrayBuffer): ParsedDbf {
  try {
    return parseDbfUnchecked(buffer);
  } catch (err) {
    // Same rationale as shpParser.ts's own wrapper: a corrupt/truncated
    // .dbf already fails via a native, bounds-checked `RangeError` rather
    // than silently reading past the buffer — re-thrown here as a clear,
    // descriptive error instead.
    if (err instanceof RangeError) {
      throw new Error("Malformed .dbf: unexpected end of file");
    }
    throw err;
  }
}

function parseDbfUnchecked(buffer: ArrayBuffer): ParsedDbf {
  const view = new DataView(buffer);
  const version = view.getUint8(0);
  if (version !== 0x03) {
    throw new Error(
      `Unsupported .dbf version 0x${version.toString(16)} (only dBase III, 0x03, is supported)`
    );
  }
  const recordCount = view.getInt32(4, true);
  const headerLength = view.getUint16(8, true);
  const recordLength = view.getUint16(10, true);
  // Must reject: a zero record length makes every record read the exact
  // same offset, never running out of bounds — a huge recordCount would
  // hang allocating rows forever, uncaught by any check below.
  if (recordLength <= 0) {
    throw new Error(`Malformed .dbf: non-positive record length (${recordLength})`);
  }

  const fields: DbfField[] = [];
  let descriptorOffset = 32;
  while (view.getUint8(descriptorOffset) !== 0x0d) {
    const name = readAsciiZ(view, descriptorOffset, 11);
    const type = String.fromCharCode(view.getUint8(descriptorOffset + 11));
    if (!isDbfFieldType(type)) {
      throw new Error(`Unsupported .dbf field type "${type}" for field "${name}"`);
    }
    const length = view.getUint8(descriptorOffset + 16);
    const decimalCount = view.getUint8(descriptorOffset + 17);
    fields.push({ name, type, length, decimalCount });
    descriptorOffset += 32;
  }

  const rows: Record<string, string>[] = [];
  const decoder = new TextDecoder("ascii");
  for (let i = 0; i < recordCount; i++) {
    const recordOffset = headerLength + i * recordLength;
    // Must bound by the buffer's real size, not trust a declared
    // recordCount blindly — a fake huge count could force unbounded
    // allocation otherwise.
    if (recordOffset + recordLength > view.byteLength) break;
    const deletionFlag = view.getUint8(recordOffset);
    if (deletionFlag === 0x2a /* '*' */) continue;

    const row: Record<string, string> = {};
    let fieldOffset = recordOffset + 1;
    for (const field of fields) {
      const bytes = new Uint8Array(view.buffer, view.byteOffset + fieldOffset, field.length);
      row[field.name] = decoder.decode(bytes).trim();
      fieldOffset += field.length;
    }
    rows.push(row);
  }

  return { fields, rows };
}
